import * as Resolve from '../Resolve';

import { Roles } from '../../common/';
import { IGroupService } from '../../services';
import { IWideState } from '../../IWideState';

const states: IWideState[] = [
    {
        name: 'group',
        abstract: true,
        url: '/group/:groupGuid/:beginDate/:endDate',
        component: 'wtGroupState',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer, Roles.operator]),
            group: [
                'groupService', '$stateParams', (groupService: IGroupService, $stateParams) => {
                    return groupService.getByGuid($stateParams['groupGuid']).then(dto => dto.group);
                }
            ]
        },
        params: {
            beginDate: '',
            endDate: ''
        },
        data: {
            sortingOrder: 0,
            displayName: 'Model',
            editButtonVisibility: () => false,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: ''
            }
        }
    }, {
        name: 'group.general',
        url: '/general',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            displayName: 'Overview',
            icon: 'fa-th-large',
            editButtonVisibility: () => true,
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Overview'
            },
            sortingOrder: 10
        },
        views: {
            main: {
                template: '<wt-general-state class="flex-directive" selected-group="vm.selectedGroup" gridster-options="vm.gridsterOptions"></wt-general-state>'
            },
            contextual: {
                template: `<wt-modern-model-contextual app-config="vm.appConfiguration"
                                                       form-controller="vm.formController"
                                                       groups="vm.allGroups"
                                                       ng-if="vm.isEditMode"
                                                       selected-item="vm.selectedGroup"
                                                       reference-group-dictionary="vm.validationPagesMapping"
                                                       templates="vm.templates">
                           </wt-modern-model-contextual>`
            }
        }
    }, {
        name: 'group.validation',
        url: '/validation/:maximized?',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer, Roles.operator])
        },
        data: {
            displayName: 'Validation',
            icon: 'fa-check-square-o',
            editButtonVisibility: () => true,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Validation'
            },
            sortingOrder: 20
        },
        params: {
            maximized: 'false'
        },
        views: {
            main: {
                template: require('./groupValidationMain.html')
            },
            contextual: {
                template: require('./groupValidationContextual.html')
            }
        }
    }, {
        name: 'group.trend',
        url: '/trend',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer, Roles.operator])
        },
        data: {
            displayName: 'Group trend',
            icon: 'fa-line-chart',
            editButtonVisibility: () => true,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Group trend'
            },
            sortingOrder: 30
        },
        views: {
            main: {
                template: '<wt-group-trend-state class="flex-directive" form-controller="vm.formController" group="vm.selectedGroup" data="vm.data"></wt-group-trend-state>'
            },
            contextual: {
                template: '<wt-modern-group-trend-contextual form-controller="vm.formController" data="vm.data" group="vm.selectedGroup"></wt-group-trend-contextual>'
            }
        }
    }, {
        name: 'group.script',
        url: '/script',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer, Roles.operator])
        },
        data: {
            displayName: 'Script',
            icon: 'fa-code',
            editButtonVisibility: () => true,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Script'
            },
            sortingOrder: 40
        },
        views: {
            main: {
                template: require('./script/scriptMainView.html')
            },
            contextual: {
                template: require('./script/scriptContextualView.html')
            }
        }
    }, {
        name: 'group.reconciliation',
        url: '/reconciliation',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer, Roles.operator])
        },
        data: {
            displayName: 'Reconciliation',
            icon: 'fa-caret-square-o-right',
            editButtonVisibility: () => true,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Reconciliation'
            },
            sortingOrder: 50
        },
        views: {
            main: {
                template: require('./reconciliationState/reconciliationMainView.html')
            },
            contextual: {
                template: require('./script/scriptContextualView.html')
            }
        }
    }, {
        name: 'group.identifiers',
        url: '/identifiers',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            displayName: 'Group identifiers',
            icon: 'fa-table',
            editButtonVisibility: () => true,
            editRoles: [Roles.engineer],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Identifiers'
            },
            sortingOrder: 60
        },
        views: {
            main: {
                template: '<wt-identifiers-state class="flex-directive" selected-group="vm.selectedGroup" selected-item="vm.selectedItem" contextual-form="vm.formController" can-edit="vm.isEditable"></wt-identifiers-state>'
            },
            contextual: {
                template: '<wt-modern-identifier-contextual app-config="vm.appConfiguration" selected-item="vm.selectedItem" selected-group="vm.selectedGroup" form-controller="vm.formController" has-reconciliation="vm.selectedGroup.reconciliationMatrix && vm.selectedGroup.reconciliationMatrix.length"></wt-modern-identifier-contextual>'
            }
        }
    }, {
        name: 'group.alarms',
        url: '/alarms/:identifierGuid?',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            displayName: 'Group alarms',
            icon: 'fa-bell',
            editButtonVisibility: () => false,
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Alarms'
            },
            sortingOrder: 70
        },
        views: {
            main: {
                template: '<wt-group-alarms-state class="flex-directive" selected-group="vm.selectedGroup" selected-identifier="vm.selectedIdentifier" can-edit="vm.canEdit"></wt-group-alarms-state>'
            }
        },
        params: {
            identifierGuid: ''
        }
    }, {
        name: 'group.rules',
        url: '/rules',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            displayName: 'Group Rules',
            icon: 'fa-signal',
            editButtonVisibility: () => true,
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Rules'
            },
            sortingOrder: 80
        },
        views: {
            main: {
                template: '<wt-group-rules-state class="flex-directive" group="vm.selectedGroup"></wt-group-rules-state>'
            },
            contextual: {
                template: '<wt-group-rules-contextual></wt-group-rules-contextual>'
            }
        }
    }, {
        name: 'group.analysis',
        url: '/analysis/:identifierGuid?',
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            displayName: 'Group analysis',
            icon: 'fa-area-chart',
            editButtonVisibility: () => false,
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Analysis'
            },
            sortingOrder: 90
        },
        views: {
            main: {
                template: '<wt-identifier-analysis-state class="flex-directive" selected-group="vm.selectedGroup"></wt-identifier-analysis-state>'
            }
        },
        params: {
            identifierGuid: ''
        }
    }, {
        name: `group.resultsStatus`,
        url: `/resultsStatus`,
        resolve: {
            auth: Resolve.authResolve,
            connected: Resolve.connectedResolve,
            roles: Resolve.rolesResolve([Roles.master, Roles.engineer])
        },
        data: {
            sortingOrder: 100,
            displayName: 'Results status',
            icon: 'fa-clock-o',
            editButtonVisibility: () => false,
            editRoles: [],
            breadcrumbs: {
                level3StateName: 'Model',
                level4: '',
                level5: 'Results status'
            }
        },
        views: {
            main: {
                template: '<wt-group-results-status-state class="flex-directive"></wt-group-results-status-state>'
            }
        }
    }
];

export default states;